import type { ToolWithHandler } from '../L3-services/llm/providerFactory.js'
import { ensureDirectorySync, writeTextFileSync } from '../L1-infra/fileSystem/fileSystem.js'
import { join, dirname } from '../L1-infra/paths/paths.js'
import { BaseAgent } from './BaseAgent'
import logger from '../L1-infra/logger/configLogger'
import type { MCPServerConfig } from '../L3-services/llm/providerFactory.js'
import { getConfig } from '../L1-infra/config/environment.js'
import {
  Platform,
  ShortClip,
  SocialPost,
  Transcript,
  VideoFile,
  VideoSummary,
} from '../L0-pure/types/index'
import type { Idea, ToneStrategy } from '../L0-pure/types/index.js'
import { buildIdeaContextForPosts } from '../L0-pure/ideaContext/ideaContext.js'

// ── JSON shape the LLM returns via the create_posts tool ────────────────────

interface PlatformPost {
  platform: string
  content: string
  hashtags: string[]
  links: string[]
  characterCount: number
}

interface CreatePostsArgs {
  posts: PlatformPost[]
}

// ── System prompt───────────────────────────────────────────────────────────

const PER_PLATFORM_GUIDELINES = `Platform guidelines:
1. **TikTok** – Casual, hook-driven, trending hashtags, 150 chars max, emoji-heavy.
2. **YouTube** – Descriptive, SEO-optimized title + description, relevant tags.
3. **Instagram** – Visual storytelling, emoji-rich, 30 hashtags max, engaging caption.
4. **LinkedIn** – Professional, thought-leadership, industry insights, 1-3 hashtags.
5. **X (Twitter)** – Concise, punchy, 280 chars max, 2-5 hashtags, thread-ready.`

const UNIFIED_TONE_GUIDELINES = `Platform guidelines:
For all platforms, use the same professional but approachable tone. Keep posts concise, informative, and authentic. Use the same core message — adapt only for platform-specific formatting constraints (character limits, hashtag placement).
1. **TikTok** – 150 chars max, include relevant hashtags.
2. **YouTube** – SEO-optimized title + description, relevant tags.
3. **Instagram** – 30 hashtags max, engaging caption.
4. **LinkedIn** – 1-3 hashtags, professional framing.
5. **X (Twitter)** – 280 chars max, 2-5 hashtags.`

function buildSocialMediaSystemPrompt(toneStrategy?: ToneStrategy): string {
  const platformGuidelines = toneStrategy === 'unified' ? UNIFIED_TONE_GUIDELINES : PER_PLATFORM_GUIDELINES

  return `You are a viral social-media content strategist.
Given a video transcript and summary you MUST generate one post for each of the 5 platforms listed below.
Each post must match the platform's format and constraints exactly.

${platformGuidelines}

IMPORTANT – Content format:
The "content" field you provide must be the FINAL, ready-to-post text that can be directly copied and pasted onto the platform. Do NOT use markdown headers, bullet points, or any formatting inside the content. Include hashtags inline at the end of the post text where appropriate. The content is saved as-is for direct posting.

Workflow:
1. First use the "web_search_exa" tool to search for relevant URLs based on the key topics discussed in the video.
2. Then call the "create_posts" tool with a JSON object that has a "posts" array.
   Each element must have: platform, content, hashtags (array), links (array), characterCount.

Include relevant links in posts when search results provide them.
Always call "create_posts" exactly once with all 5 platform posts.`
}

const SYSTEM_PROMPT = buildSocialMediaSystemPrompt()

// ── Agent ────────────────────────────────────────────────────────────────────

class SocialMediaAgent extends BaseAgent {
  private collectedPosts: PlatformPost[] = []

  constructor(systemPrompt: string = SYSTEM_PROMPT, model?: string) {
    super('SocialMediaAgent', systemPrompt, undefined, model)
  }

  protected resetForRetry(): void {
    this.collectedPosts = []
  }

  protected getMcpServers(): Record<string, MCPServerConfig> | undefined {
    const config = getConfig()
    if (!config.EXA_API_KEY) return undefined
    return {
      exa: {
        type: 'http' as const,
        url: `${config.EXA_MCP_URL}?exaApiKey=${config.EXA_API_KEY}&tools=web_search_exa`,
        headers: {},
        tools: ['*'],
      },
    }
  }

  protected getTools(): ToolWithHandler[] {
    return [
      {
        name: 'create_posts',
        description:
          'Submit the generated social media posts for all 5 platforms.',
        parameters: {
          type: 'object',
          properties: {
            posts: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  platform: { type: 'string' },
                  content: { type: 'string' },
                  hashtags: { type: 'array', items: { type: 'string' } },
                  links: { type: 'array', items: { type: 'string' } },
                  characterCount: { type: 'number' },
                },
                required: ['platform', 'content', 'hashtags', 'links', 'characterCount'],
              },
              description: 'Array of posts, one per platform',
            },
          },
          required: ['posts'],
        },
        handler: async (args: unknown) => {
          const { posts } = args as CreatePostsArgs
          for (const post of posts) {
            if (post.platform.toLowerCase() === 'instagram' && post.hashtags.length > 30) {
              logger.warn(`[SocialMediaAgent] Instagram post has ${post.hashtags.length} hashtags, trimming to 30`)
              post.hashtags = post.hashtags.slice(0, 30)
            }
          }
          this.collectedPosts = posts
          logger.info(`[SocialMediaAgent] create_posts received ${posts.length} posts`)
          return JSON.stringify({ success: true, count: posts.length })
        },
      },
    ]
  }

  protected async handleToolCall(
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<unknown> {
    // Tool dispatch is handled inline via tool handlers above.
    // This satisfies the abstract contract from BaseAgent.
    logger.warn(`[SocialMediaAgent] Unexpected handleToolCall for "${toolName}"`)
    return { error: `Unknown tool: ${toolName}` }
  }

  getCollectedPosts(): PlatformPost[] {
    return this.collectedPosts
  }
}

// ── Helper: map raw platform string → Platform enum ─────────────────────────

function toPlatformEnum(raw: string): Platform {
  const normalised = raw.toLowerCase().trim()
  switch (normalised) {
    case 'tiktok':
      return Platform.TikTok
    case 'youtube':
      return Platform.YouTube
    case 'instagram':
      return Platform.Instagram
    case 'linkedin':
      return Platform.LinkedIn
    case 'x':
    case 'twitter':
    case 'x (twitter)':
    case 'x/twitter':
      return Platform.X
    default:
      return normalised as Platform
  }
}

// ── Helper: render a post file with YAML frontmatter ───────────────────────

interface RenderPostOpts {
  videoSlug: string
  shortSlug?: string | null
}

function renderPostFile(post: PlatformPost, opts: RenderPostOpts): string {
  const now = new Date().toISOString()
  const platform = toPlatformEnum(post.platform)
  const lines: string[] = ['---']

  lines.push(`platform: ${platform}`)
  lines.push(`status: draft`)
  lines.push(`scheduledDate: null`)

  if (post.hashtags.length > 0) {
    lines.push('hashtags:')
    for (const tag of post.hashtags) {
      lines.push(`  - "${tag}"`)
    }
  } else {
    lines.push('hashtags: []')
  }

  if (post.links.length > 0) {
    lines.push('links:')
    for (const link of post.links) {
      lines.push(`  - url: "${link}"`)
      lines.push(`    title: null`)
    }
  } else {
    lines.push('links: []')
  }

  lines.push(`characterCount: ${post.characterCount}`)
  lines.push(`videoSlug: "${opts.videoSlug}"`)
  lines.push(`shortSlug: ${opts.shortSlug ? `"${opts.shortSlug}"` : 'null'}`)
  lines.push(`createdAt: "${now}"`)
  lines.push('---')
  lines.push('')
  lines.push(post.content)
  lines.push('')

  return lines.join('\n')
}

// ── Public API ──────────────────────────────────────────────────────────────

export async function generateShortPosts(
  video: VideoFile,
  short: ShortClip,
  transcript: Transcript,
  model?: string,
  summary?: VideoSummary,
): Promise<SocialPost[]> {
  const agent = new SocialMediaAgent(undefined, model)

  try {
    // Extract transcript segments that overlap with the short's time ranges
    const relevantText = transcript.segments
      .filter((seg) =>
        short.segments.some((ss) => seg.start < ss.end && seg.end > ss.start),
      )
      .map((seg) => seg.text)
      .join(' ')

    const messageParts = [
      '## Short Clip Metadata',
      `- **Title:** ${short.title}`,
      `- **Description:** ${short.description}`,
      `- **Duration:** ${short.totalDuration.toFixed(1)}s`,
      `- **Tags:** ${short.tags.join(', ')}`,
    ]

    // Include broader video context when available
    if (summary) {
      messageParts.push(
        '',
        '## Broader Video Context',
        `This clip is from a longer video titled "${summary.title}".`,
        `**Video overview:** ${summary.overview}`,
        `**Key topics covered in the full video:** ${summary.keyTopics.join(', ')}`,
        '',
        'Use this context to position the clip within the larger narrative. The post should tease the broader topic while highlighting what makes this specific clip compelling on its own.',
      )
    }

    messageParts.push(
      '',
      '## Relevant Transcript',
      relevantText.slice(0, 3000),
    )

    const userMessage = messageParts.join('\n')

    await agent.run(userMessage)

    const collectedPosts = agent.getCollectedPosts()

    // Save posts to recordings/{slug}/shorts/{short-slug}/posts/
    const shortsDir = join(dirname(video.repoPath), 'shorts')
    const postsDir = join(shortsDir, short.slug, 'posts')
    ensureDirectorySync(postsDir)

    const socialPosts: SocialPost[] = collectedPosts.map((p) => {
      const platform = toPlatformEnum(p.platform)
      const outputPath = join(postsDir, `${platform}.md`)

      writeTextFileSync(
        outputPath,
        renderPostFile(p, { videoSlug: video.slug, shortSlug: short.slug }),
      )
      logger.info(`[SocialMediaAgent] Wrote short post ${outputPath}`)

      return {
        platform,
        content: p.content,
        hashtags: p.hashtags,
        links: p.links,
        characterCount: p.characterCount,
        outputPath,
      }
    })

    return socialPosts
  } finally {
    await agent.destroy()
  }
}

export async function generateSocialPosts(
  video: VideoFile,
  transcript: Transcript,
  summary: VideoSummary,
  outputDir?: string,
  model?: string,
  ideas?: Idea[],
  toneStrategy?: ToneStrategy,
): Promise<SocialPost[]> {
  const basePrompt = toneStrategy ? buildSocialMediaSystemPrompt(toneStrategy) : SYSTEM_PROMPT
  const systemPrompt = basePrompt + (ideas?.length ? buildIdeaContextForPosts(ideas) : '')
  const agent = new SocialMediaAgent(systemPrompt, model)

  try {
    // Build the user prompt with transcript summary and metadata
    const userMessage = [
      '## Video Metadata',
      `- **Title:** ${summary.title}`,
      `- **Slug:** ${video.slug}`,
      `- **Duration:** ${video.duration}s`,
      '',
      '## Summary',
      summary.overview,
      '',
      '## Key Topics',
      summary.keyTopics.map((t) => `- ${t}`).join('\n'),
      '',
      '## Transcript (first 3000 chars)',
      transcript.text.slice(0, 3000),
    ].join('\n')

    await agent.run(userMessage)

    const collectedPosts = agent.getCollectedPosts()

    // Ensure the output directory exists
    const outDir = outputDir ?? join(video.videoDir, 'social-posts')
    ensureDirectorySync(outDir)

    const socialPosts: SocialPost[] = collectedPosts.map((p) => {
      const platform = toPlatformEnum(p.platform)
      const outputPath = join(outDir, `${platform}.md`)

      writeTextFileSync(
        outputPath,
        renderPostFile(p, { videoSlug: video.slug }),
      )
      logger.info(`[SocialMediaAgent] Wrote ${outputPath}`)

      return {
        platform,
        content: p.content,
        hashtags: p.hashtags,
        links: p.links,
        characterCount: p.characterCount,
        outputPath,
      }
    })

    return socialPosts
  } finally {
    await agent.destroy()
  }
}
